import { getSpecialties, SpecialtyBranch } from '../services/accountSecurityService';
import DocumentoClienteFields from '../components/common/DocumentoClienteFields';
import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Loading from '../components/Loading';
import { registerUser } from '../services/authService';
import { useTheme } from '../context/ThemeContext';
import DarkModeToggle from '../components/common/DarkModeToggle';
import BorderGlow from '../components/common/BorderGlow';
import BrandStripes from '../components/common/BrandStripes';
import HexagonCornerDecoration from '../components/common/HexagonCornerDecoration';
import inicio from '../assets/images/auth/inicio.png';
import logo from '../assets/images/logo/logo.png';
import { Lock, LockOpen, Mail, User, ArrowRight, CheckCircle, Briefcase, IdCard } from 'lucide-react';

const logoDark = `${process.env.PUBLIC_URL}/logo-comarca-blanco.png?v=2`;

const colors = {
    primary: '#3564db',
    primaryDark: '#420f54',
    primaryLight: '#3564db',
    secondary: '#3564db',
    secondaryLight: '#92b1ed',
    accent: '#ffd97c',
    accentDark: '#EA8406',
};

const Register = () => {
    const { isDark } = useTheme();
    const [formData, setFormData] = useState({
        first_name: '',
        last_name: '',
        email: '',
        password: '',
        especialidad: '',
        telefono: '', ci_nit: '', codigo_tipo_documento_identidad_sin: 1, complemento: '',
    });
    const [error, setError] = useState('');
    const [emailError, setEmailError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [focused, setFocused] = useState<string | null>(null);
    const navigate = useNavigate();

    const [specialties, setSpecialties] = useState<SpecialtyBranch[]>([]);
    const [specialtiesLoading, setSpecialtiesLoading] = useState(true);
    const [specialtiesError, setSpecialtiesError] = useState(false);
    const loadSpecialties = () => {setSpecialtiesLoading(true);setSpecialtiesError(false);getSpecialties().then(setSpecialties).catch(()=>setSpecialtiesError(true)).finally(()=>setSpecialtiesLoading(false));};
    useEffect(() => { let active=true; getSpecialties().then(data=>{if(active)setSpecialties(data);}).catch(()=>{if(active)setSpecialtiesError(true);}).finally(()=>{if(active)setSpecialtiesLoading(false);});return()=>{active=false;}; }, []);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setEmailError('');
        setIsLoading(true);
        try {
            await registerUser(formData);
            navigate('/login');
        } catch (err: any) {
            const data = err.response?.data;
            const messages = data && typeof data === 'object' ? Object.values(data).flat().join(' ') : '';
            const message = messages || err.message || 'Error al registrar usuario';
            if (data?.email) {
                setEmailError(message);
            } else {
                setError(message);
            }
        } finally {
            setIsLoading(false);
        }
    };

    const inputStyle = (field: string) => ({
        width: '100%',
        padding: '12px 16px',
        borderRadius: '14px',
        border: focused === field
            ? (isDark ? '1.5px solid #3564db' : '1.5px solid #3564db')
            : (isDark ? '1.5px solid rgba(193,52,139,0.3)' : '1.5px solid #f1f5f9'),
        background: focused === field
            ? (isDark ? '#1f1333' : '#fff')
            : (isDark ? '#120920' : '#f8fafc'),
        fontSize: '0.88rem',
        color: isDark ? '#ffffff' : '#0f172a',
        fontFamily: "'DM Sans', sans-serif",
        boxShadow: focused === field ? '0 0 0 3px rgba(193,52,139,0.25)' : 'none',
        outline: 'none',
        transition: 'all 0.2s cubic-bezier(.16,1,.3,1)',
    });

    const inputWithIconStyle = (field: string, side: 'left' | 'right' = 'left') => ({
        ...inputStyle(field),
        paddingLeft: side === 'left' ? '42px' : '16px',
        paddingRight: side === 'right' ? '44px' : '16px',
    });

    const passwordStrength = () => {
        const p = formData.password;
        if (!p) return null;
        const score = [p.length >= 8, /[A-Z]/.test(p), /[0-9]/.test(p), /[^A-Za-z0-9]/.test(p)].filter(Boolean).length;
        const map = [
            { label: 'Muy débil', color: '#3564db', width: '25%' },
            { label: 'Débil', color: '#EA8406', width: '50%' },
            { label: 'Buena', color: '#3564db', width: '75%' },
            { label: 'Fuerte', color: isDark ? '#FFD97C' : '#3564db', width: '100%' },
        ];
        return map[score - 1] ?? map[0];
    };
    const strength = passwordStrength();

    return (
        <div style={{
            fontFamily: "'DM Sans', sans-serif",
            minHeight: '100vh',
            display: 'flex',
            background: isDark ? '#0b0614' : '#f8fafc',
            position: 'relative',
            transition: 'background 0.3s ease',
        }}>
            {/* Toggle de modo oscuro en la esquina superior */}
            <div style={{ position: 'fixed', top: '20px', right: '20px', zIndex: 100 }}>
                <DarkModeToggle />
            </div>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700;800;900&display=swap');
                * { box-sizing: border-box; margin: 0; padding: 0; }
                @keyframes blobFloat {
                    0%, 100% { border-radius: 60% 40% 30% 70% / 60% 30% 70% 40%; }
                    50% { border-radius: 30% 60% 70% 40% / 50% 60% 30% 60%; }
                }
                @keyframes fadeUp {
                    from { opacity: 0; transform: translateY(20px); }
                    to   { opacity: 1; transform: translateY(0); }
                }
                @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
                @keyframes spin { to { transform: rotate(360deg); } }
                @keyframes pulsePurple {
                    0%, 100% { box-shadow: 0 0 0 0 rgba(97,37,123,0.35); }
                    50%       { box-shadow: 0 0 0 6px rgba(97,37,123,0); }
                }
                .blob-reg { animation: blobFloat 11s ease-in-out infinite; }
                .reg-input::placeholder { color: #94a3b8; }
                .reg-btn:hover:not(:disabled) { transform: translateY(-2px); box-shadow: 0 12px 32px rgba(97,37,123,0.4) !important; }
                .reg-btn:active { transform: translateY(0); }
                .reg-link:hover { color: #3564db !important; }
                @media (min-width: 768px) { .md-panel { display: flex !important; } }
            `}</style>

            {isLoading && <Loading text="Creando cuenta..." fullScreen={true} />}
            {/* ── Panel izquierdo (decorativo inspirado en el Landing Page) ── */}
            <div className="md-panel" style={{
                display: 'none', flex: '0 0 50%',
                position: 'relative', overflow: 'hidden',
                background: isDark
                    ? 'linear-gradient(135deg, #0b0614 0%, #150a28 50%, #1f0b36 100%)'
                    : 'linear-gradient(135deg, #fdfbfd 0%, #faf3fe 50%, #f3e5f8 100%)',
                borderRight: isDark ? '1.5px solid rgba(193,52,139,0.22)' : '1.5px solid #ebdcf0',
                alignItems: 'center', justifyContent: 'center',
            }}>
                {/* Malla hexagonal minimalista con degradé horizontal suave */}
                <HexagonCornerDecoration
                    variant="horizontal-degrade"
                    direction="left-to-right"
                    minimal={true}
                    opacity={isDark ? 0.38 : 0.28}
                />

                {/* Resplandor ambiental suave y sutil */}
                <div style={{
                    position: 'absolute', top: '-60px', right: '-60px',
                    width: '380px', height: '380px', pointerEvents: 'none', zIndex: 0,
                    background: isDark
                        ? `radial-gradient(circle, rgba(193,52,139,0.14) 0%, transparent 70%)`
                        : `radial-gradient(circle, rgba(97,37,123,0.08) 0%, transparent 70%)`,
                }} />
                <div style={{
                    position: 'absolute', bottom: '-60px', left: '-60px',
                    width: '340px', height: '340px', pointerEvents: 'none', zIndex: 0,
                    background: isDark
                        ? `radial-gradient(circle, rgba(255,217,124,0.08) 0%, transparent 70%)`
                        : `radial-gradient(circle, rgba(255,217,122,0.12) 0%, transparent 70%)`,
                    borderRadius: '50%',
                }} />

                {/* Barra de colores corporativos bottom */}
                <div style={{
                    position: 'absolute', bottom: 0, left: 0, right: 0, height: '3px', zIndex: 10,
                    background: `linear-gradient(90deg, #3564db, #3564db, #3564db, #FFD97C, #EA8406)`,
                }} />

                <div style={{ position: 'relative', textAlign: 'center', padding: '36px 32px', maxWidth: '490px', zIndex: 2 }}>
                    {/* Badge */}
                    <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: '8px',
                        background: isDark ? 'rgba(193,52,139,0.18)' : 'rgba(97,37,123,0.08)',
                        border: isDark ? '1.5px solid rgba(193,52,139,0.35)' : '1.5px solid rgba(97,37,123,0.2)',
                        backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)',
                        borderRadius: '100px', padding: '6px 18px',
                        fontSize: '0.72rem', fontWeight: '800',
                        color: isDark ? '#FFD97C' : '#3564db',
                        letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '20px',
                    }}>
                        <span style={{
                            width: '7px', height: '7px', borderRadius: '50%',
                            background: isDark ? '#FFD97C' : '#3564db', display: 'inline-block',
                            boxShadow: isDark ? '0 0 8px #FFD97C' : '0 0 8px #3564db',
                        }} />
                        Únete a Co Marca · Membresía
                    </span>

                    {/* Showcase Fotográfico */}
                    <div style={{
                        position: 'relative',
                        borderRadius: '24px', overflow: 'hidden',
                        boxShadow: isDark ? '0 24px 60px -10px rgba(0,0,0,0.8), 0 0 30px rgba(97,37,123,0.2)' : '0 24px 60px -10px rgba(97,37,123,0.18)',
                        marginBottom: '22px',
                        border: isDark ? '1.5px solid rgba(193,52,139,0.3)' : '1.5px solid rgba(255,255,255,0.8)',
                    }}>
                        <img
                            src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?auto=format&fit=crop&w=1000&q=80"
                            alt="Espacios Co Marca"
                            style={{ width: '100%', height: '210px', objectFit: 'cover', display: 'block' }}
                        />
                        <div style={{
                            position: 'absolute', inset: 0,
                            background: 'linear-gradient(to top, rgba(11,6,20,0.88) 0%, rgba(11,6,20,0.25) 55%, transparent 100%)',
                            pointerEvents: 'none',
                        }} />

                        {/* Tag de ubicación y beneficio */}
                        <div style={{
                            position: 'absolute', top: '12px', left: '12px',
                            background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(8px)',
                            WebkitBackdropFilter: 'blur(8px)',
                            border: '1px solid rgba(255,255,255,0.2)',
                            borderRadius: '8px', padding: '4px 10px',
                            fontSize: '0.7rem', fontWeight: '700', color: '#fff',
                            display: 'flex', alignItems: 'center', gap: '5px',
                        }}>
                            <span>✨</span> Membresía Abierta
                        </div>

                        {/* Pill de comunidad */}
                        <div style={{
                            position: 'absolute', bottom: '10px', left: '12px', right: '12px',
                            background: 'rgba(255,255,255,0.14)', backdropFilter: 'blur(14px)',
                            WebkitBackdropFilter: 'blur(14px)',
                            border: '1px solid rgba(255,255,255,0.25)',
                            borderRadius: '12px', padding: '8px 12px',
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                            color: '#fff',
                        }}>
                            <span style={{ fontSize: '0.74rem', fontWeight: '700' }}>Sin costos de inscripción</span>
                            <span style={{ fontSize: '0.68rem', fontWeight: '700', color: '#92b1ed', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#92b1ed', boxShadow: '0 0 6px #92b1ed' }} />
                                Activación 100% online
                            </span>
                        </div>
                    </div>

                    <h2 style={{
                        fontSize: 'clamp(1.4rem, 2.3vw, 1.85rem)', fontWeight: '900',
                        color: isDark ? '#ffffff' : '#0f172a', letterSpacing: '-0.025em', lineHeight: 1.15, marginBottom: '8px',
                    }}>
                        Tu espacio de <span style={{
                            background: 'linear-gradient(135deg, #3564db 0%, #EA8406 100%)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                        }}>crecimiento</span> en La Paz
                    </h2>
                    <p style={{ fontSize: '0.88rem', color: isDark ? '#cbd5e1' : '#64748b', lineHeight: '1.6', fontWeight: '400', textAlign: 'center', marginBottom: '16px' }}>
                        Crea tu cuenta gratuita y accede a oficinas privadas, puestos de coworking, salas de reuniones y la red empresarial más activa.
                    </p>

                    {/* Beneficios clave con micro-tarjetas */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', textAlign: 'left', marginBottom: '18px' }}>
                        {[
                            { title: 'Espacios Flexibles', desc: 'Reserva horas de cowork y salas según lo que necesites', color: '#3564db' },
                            { title: 'Networking VIP', desc: 'Conéctate con más de 300 profesionales y empresas', color: '#3564db' },
                            { title: 'Gestión Inmediata', desc: 'Acceso instantáneo a reservas y correspondencia', color: '#3564db' },
                        ].map(({ title, desc, color }) => (
                            <div
                                key={title}
                                style={{
                                    display: 'flex', alignItems: 'center', gap: '10px',
                                    padding: '8px 12px', borderRadius: '12px',
                                    background: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(97,37,123,0.03)',
                                    border: isDark ? '1px solid rgba(193,52,139,0.2)' : '1px solid #edf2f7',
                                }}
                            >
                                <div style={{
                                    width: '24px', height: '24px', borderRadius: '8px', flexShrink: 0,
                                    background: `${color}18`, border: `1px solid ${color}30`,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                }}>
                                    <CheckCircle size={13} color={color} strokeWidth={2.5} />
                                </div>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <span style={{ fontSize: '0.8rem', color: isDark ? '#f8fafc' : '#1e293b', fontWeight: '700' }}>{title}: </span>
                                    <span style={{ fontSize: '0.75rem', color: isDark ? '#94a3b8' : '#64748b' }}>{desc}</span>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Stats */}
                    <div style={{
                        display: 'flex', justifyContent: 'center', gap: '0',
                        background: isDark ? '#150d26' : '#ffffff',
                        borderRadius: '16px', border: isDark ? '1.5px solid rgba(193,52,139,0.28)' : '1.5px solid #edf2f7',
                        boxShadow: isDark ? '0 8px 30px rgba(0,0,0,0.45)' : '0 8px 24px rgba(97,37,123,0.06)', overflow: 'hidden',
                    }}>
                        {[
                            { v: '300+', l: 'Miembros', c: isDark ? '#FFD97C' : '#3564db' },
                            { v: '120+', l: 'Proyectos', c: '#3564db' },
                            { v: '100%', l: 'Flexible', c: '#3564db' },
                        ].map((s, i) => (
                            <div key={s.l} style={{
                                flex: 1, padding: '10px 8px', textAlign: 'center',
                                borderRight: i < 2 ? (isDark ? '1.5px solid rgba(193,52,139,0.2)' : '1.5px solid #f1f5f9') : 'none',
                            }}>
                                <div style={{ fontSize: '1.15rem', fontWeight: '900', color: s.c, lineHeight: 1 }}>{s.v}</div>
                                <div style={{ fontSize: '0.66rem', color: isDark ? '#cbd5e1' : '#94a3b8', fontWeight: '600', marginTop: '3px' }}>{s.l}</div>
                            </div>
                        ))}
                    </div>

                    {/* BrandStripes decorativo */}
                    <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'center' }}>
                        <BrandStripes size="sm" />
                    </div>
                </div>
            </div>

            {/* ── Panel derecho (formulario con BorderGlow) ── */}
            <div style={{
                flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
                padding: 'clamp(20px, 4vw, 40px)',
                background: isDark ? '#0b0614' : '#fff',
                position: 'relative', overflow: 'hidden',
                transition: 'background 0.3s ease',
            }}>
                {/* Blob fondo */}
                <div style={{
                    position: 'absolute', top: '-120px', right: '-120px',
                    width: '400px', height: '400px', pointerEvents: 'none',
                    background: isDark
                        ? `radial-gradient(circle, rgba(193,52,139,0.12), transparent 70%)`
                        : `radial-gradient(circle, rgba(97,37,123,0.06), transparent 70%)`,
                    borderRadius: '50%',
                }} />

                <div style={{
                    width: '100%', maxWidth: '440px', position: 'relative',
                }}>
                    <BorderGlow
                        borderRadius={24}
                        backgroundColor={isDark ? '#150d24' : '#ffffff'}
                    >
                        <div style={{ padding: 'clamp(24px, 3.5vw, 36px)', width: '100%', position: 'relative' }}>
                            {/* Enlace para volver a la landing page */}
                            <div style={{ marginBottom: '14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Link
                                    to="/"
                                    style={{
                                        display: 'inline-flex', alignItems: 'center', gap: '6px',
                                        fontSize: '0.78rem', fontWeight: '700',
                                        color: isDark ? '#FFD97C' : '#3564db',
                                        textDecoration: 'none',
                                        transition: 'all 0.2s ease',
                                    }}
                                    onMouseEnter={e => e.currentTarget.style.opacity = '0.75'}
                                    onMouseLeave={e => e.currentTarget.style.opacity = '1'}
                                >
                                    <span>←</span> Volver al inicio
                                </Link>
                                <span style={{ fontSize: '0.72rem', color: isDark ? '#94a3b8' : '#94a3b8', fontWeight: '600' }}>
                                    Co Marca Group
                                </span>
                            </div>
                            {/* Logo */}
                            <div style={{ textAlign: 'center', marginBottom: '22px', animation: 'fadeUp 0.5s ease both' }}>
                                <Link to="/" style={{ display: 'inline-block', textDecoration: 'none' }}>
                                    <img src={isDark ? logoDark : logo} alt="Co Marca" style={{
                                        height: isDark ? '30px' : '42px',
                                        margin: '0 auto',
                                        opacity: 1,
                                        transition: 'opacity 300ms ease',
                                    }}
                                        onError={e => {
                                            const el = e.currentTarget as HTMLImageElement;
                                            el.style.display = 'none';
                                            const next = el.nextElementSibling as HTMLElement;
                                            if (next) next.style.display = 'flex';
                                        }}
                                    />
                                    <div style={{ display: 'none', alignItems: 'center', gap: '10px', justifyContent: 'center' }}>
                                        <div style={{
                                            width: '38px', height: '38px', borderRadius: '11px',
                                            background: 'linear-gradient(135deg, #3564db, #3564db)',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            fontSize: '0.8rem', fontWeight: '900', color: '#fff',
                                        }}>CM</div>
                                        <span style={{ fontSize: '1.15rem', fontWeight: '900', color: isDark ? '#ffffff' : '#0f172a', letterSpacing: '-0.02em' }}>
                                            Co<span style={{ color: '#3564db' }}>Marca</span>
                                        </span>
                                    </div>
                                </Link>
                            </div>

                            {/* Header */}
                            <div style={{ marginBottom: '22px', animation: 'fadeUp 0.55s ease 0.05s both' }}>
                                <div style={{ display: 'flex', gap: '5px', marginBottom: '14px' }}>
                                    {['#3564db', '#3564db', '#3564db', '#EA8406'].map((c, i) => (
                                        <div key={i} style={{ height: '3px', width: i === 0 ? '28px' : '10px', borderRadius: '3px', background: c }} />
                                    ))}
                                </div>
                                <h1 style={{
                                    fontSize: 'clamp(1.5rem, 3vw, 1.85rem)', fontWeight: '900',
                                    color: isDark ? '#ffffff' : '#0f172a', letterSpacing: '-0.025em', lineHeight: 1.15, marginBottom: '7px',
                                }}>
                                    Crea tu <span style={{ color: isDark ? '#FFD97C' : '#3564db' }}>cuenta</span> gratis
                                </h1>
                                <p style={{ fontSize: '0.88rem', color: isDark ? '#cbd5e1' : '#64748b', fontWeight: '500', lineHeight: '1.6' }}>
                                    Solo toma un minuto · Sin tarjeta de crédito
                                </p>
                            </div>

                            {/* Form */}
                            <form onSubmit={handleSubmit} style={{ animation: 'fadeUp 0.6s ease 0.1s both' }}>

                                {/* Nombre + Apellido */}
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '14px' }}>
                                    {[
                                        { field: 'first_name', label: 'Nombre', placeholder: 'Juan', key: 'first_name' as const },
                                        { field: 'last_name', label: 'Apellido', placeholder: 'Pérez', key: 'last_name' as const },
                                    ].map(({ field, label, placeholder, key }) => (
                                        <div key={field}>
                                            <label style={{
                                                display: 'block', fontSize: '0.75rem', fontWeight: '700',
                                                color: focused === field ? (isDark ? '#FFD97C' : '#3564db') : (isDark ? '#e2e8f0' : '#374151'),
                                                marginBottom: '7px', letterSpacing: '0.02em', transition: 'color 0.2s',
                                            }}>{label}</label>
                                            <div style={{ position: 'relative' }}>
                                                <div style={{
                                                    position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)',
                                                    color: focused === field ? (isDark ? '#FFD97C' : '#3564db') : (isDark ? '#cbd5e1' : '#94a3b8'),
                                                    pointerEvents: 'none', transition: 'color 0.2s',
                                                }}>
                                                    <User size={14} strokeWidth={2} />
                                                </div>
                                                <input
                                                    type="text" required disabled={isLoading}
                                                    className="reg-input"
                                                    placeholder={placeholder}
                                                    onFocus={() => setFocused(field)}
                                                    onBlur={() => setFocused(null)}
                                                    onChange={e => setFormData({ ...formData, [key]: e.target.value })}
                                                    style={{ ...inputStyle(field), paddingLeft: '36px', width: '100%' }}
                                                />
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                {/* Email */}
                                <div style={{ marginBottom: '14px' }}>
                                    <label style={{
                                        display: 'block', fontSize: '0.75rem', fontWeight: '700',
                                        color: focused === 'email' ? (isDark ? '#FFD97C' : '#3564db') : (isDark ? '#e2e8f0' : '#374151'),
                                        marginBottom: '7px', letterSpacing: '0.02em', transition: 'color 0.2s',
                                    }}>Correo electrónico</label>
                                    <div style={{ position: 'relative' }}>
                                        <div style={{
                                            position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)',
                                            color: focused === 'email' ? (isDark ? '#FFD97C' : '#3564db') : (isDark ? '#cbd5e1' : '#94a3b8'),
                                            pointerEvents: 'none', transition: 'color 0.2s',
                                        }}>
                                            <Mail size={15} strokeWidth={2} />
                                        </div>
                                        <input
                                            type="email" required disabled={isLoading}
                                            className="reg-input"
                                            placeholder="tu@correo.com"
                                            onFocus={() => setFocused('email')}
                                            onBlur={() => setFocused(null)}
                                            onChange={e => setFormData({ ...formData, email: e.target.value })}
                                            style={inputWithIconStyle('email')}
                                        />
                                    </div>
                                    {emailError && (
                                        <p style={{
                                            display: 'flex', alignItems: 'center', gap: '5px',
                                            fontSize: '0.75rem', color: '#3564db', fontWeight: '600', marginTop: '6px',
                                            animation: 'fadeIn 0.3s ease',
                                        }}>
                                            <svg width="13" height="13" viewBox="0 0 20 20" fill="#3564db">
                                                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                            </svg>
                                            {emailError}
                                        </p>
                                    )}
                                </div>

                                {/* Especialidad o Título Específico */}
                                <div style={{ marginBottom: '14px' }}>
                                    <label style={{
                                        display: 'block', fontSize: '0.75rem', fontWeight: '700',
                                        color: focused === 'especialidad' ? (isDark ? '#FFD97C' : '#3564db') : (isDark ? '#e2e8f0' : '#374151'),
                                        marginBottom: '7px', letterSpacing: '0.02em', transition: 'color 0.2s',
                                    }}>Especialidad o Título Específico</label>
                                    <div style={{ position: 'relative' }}>
                                        <div style={{
                                            position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)',
                                            color: focused === 'especialidad' ? (isDark ? '#FFD97C' : '#3564db') : (isDark ? '#cbd5e1' : '#94a3b8'),
                                            pointerEvents: 'none', transition: 'color 0.2s',
                                        }}>
                                            <Briefcase size={15} strokeWidth={2} />
                                        </div>
                                        <select aria-label="Especialidad o Título Específico" disabled={isLoading || specialtiesLoading}
                                            className="reg-input" onFocus={()=>setFocused('especialidad')} onBlur={()=>setFocused(null)}
                                            onChange={e=>setFormData({...formData,especialidad:e.target.value})} value={formData.especialidad}
                                            style={inputWithIconStyle('especialidad')}>
                                            <option value="">{specialtiesLoading ? 'Cargando especialidades…' : 'Selecciona tu especialidad (opcional)'}</option>
                                            {specialties.map(branch=><optgroup key={branch.id} label={branch.nombre}>{branch.subramas.filter(value=>typeof value === 'string').map(value=><option key={value} value={value}>{value}</option>)}</optgroup>)}
                                        </select>
                                        {specialtiesError && <p role="alert" style={{fontSize:'.8rem',color:isDark?'#ffd97c':'#3564db',marginTop:8}}>No pudimos cargar las especialidades. <button type="button" onClick={loadSpecialties} style={{textDecoration:'underline',border:0,background:'none',color:'inherit'}}>Reintentar</button></p>}
                                    </div>
                                </div>

                                {/* Password */}
                                <div style={{ marginBottom: '6px' }}>
                                    <label style={{
                                        display: 'block', fontSize: '0.75rem', fontWeight: '700',
                                        color: focused === 'password' ? (isDark ? '#FFD97C' : '#3564db') : (isDark ? '#e2e8f0' : '#374151'),
                                        marginBottom: '7px', letterSpacing: '0.02em', transition: 'color 0.2s',
                                    }}>Contraseña</label>
                                    <div style={{ position: 'relative' }}>
                                        {/* Honeypot */}
                                        <input type="password" name="hidden-password" autoComplete="new-password"
                                            style={{ position: 'absolute', opacity: 0, height: 0, width: 0, overflow: 'hidden' }}
                                            tabIndex={-1} />
                                        <input
                                            type={showPassword ? 'text' : 'password'}
                                            required minLength={8}
                                            autoComplete="off" name="password"
                                            disabled={isLoading}
                                            className="reg-input"
                                            placeholder="Mínimo 8 caracteres"
                                            onFocus={() => setFocused('password')}
                                            onBlur={() => setFocused(null)}
                                            onChange={e => {
                                                setFormData({ ...formData, password: e.target.value });
                                                setShowPassword(false);
                                            }}
                                            value={formData.password}
                                            style={inputWithIconStyle('password', 'right')}
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPassword(!showPassword)}
                                            style={{
                                                position: 'absolute', right: '14px', top: '50%', transform: 'translateY(-50%)',
                                                background: 'none', border: 'none', cursor: 'pointer',
                                                color: focused === 'password' ? (isDark ? '#FFD97C' : '#3564db') : (isDark ? '#cbd5e1' : '#94a3b8'),
                                                transition: 'color 0.2s', padding: '2px',
                                            }}
                                        >
                                            {showPassword ? <LockOpen size={15} strokeWidth={2} /> : <Lock size={15} strokeWidth={2} />}
                                        </button>
                                    </div>

                                    {/* Barra de fortaleza */}
                                    {formData.password && strength && (
                                        <div style={{ marginTop: '8px', animation: 'fadeIn 0.3s ease' }}>
                                            <div style={{ height: '4px', background: isDark ? '#231538' : '#f1f5f9', borderRadius: '4px', overflow: 'hidden' }}>
                                                <div style={{
                                                    height: '100%', width: strength.width,
                                                    background: strength.color, borderRadius: '4px',
                                                    transition: 'all 0.3s ease',
                                                }} />
                                            </div>
                                            <p style={{ fontSize: '0.7rem', color: strength.color, fontWeight: '700', marginTop: '4px' }}>
                                                {strength.label}
                                            </p>
                                        </div>
                                    )}
                                </div>

                                {/* Error general */}
                                {error && (
                                    <div style={{
                                        display: 'flex', alignItems: 'center', gap: '10px',
                                        background: isDark ? 'rgba(193, 52, 139,0.15)' : 'rgba(97,37,123,0.08)',
                                        border: isDark ? '1.5px solid rgba(193, 52, 139,0.4)' : '1.5px solid rgba(97,37,123,0.25)',
                                        borderRadius: '12px', padding: '12px 14px', marginTop: '12px',
                                        animation: 'fadeIn 0.3s ease',
                                    }}>
                                        <div style={{
                                            width: '28px', height: '28px', borderRadius: '8px', flexShrink: 0,
                                            background: isDark ? 'rgba(193, 52, 139,0.25)' : 'rgba(97,37,123,0.14)',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        }}>
                                            <svg width="14" height="14" viewBox="0 0 20 20" fill={isDark ? '#3564db' : '#3564db'}>
                                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                            </svg>
                                        </div>
                                        <span style={{ fontSize: '0.82rem', color: isDark ? '#ff94aa' : '#420f54', fontWeight: '600' }}>{error}</span>
                                    </div>
                                )}

                                <div style={{ marginBottom: 14 }}>
                                    <label htmlFor="register-ci-nit" style={{ display: 'block', fontSize: '.75rem', fontWeight: 700, color: isDark ? '#e2e8f0' : '#374151', marginBottom: 7, letterSpacing: '.02em' }}>Número de documento (CI / NIT)</label>
                                    <div style={{ position: 'relative' }}>
                                        <IdCard size={14} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: isDark ? '#cbd5e1' : '#94a3b8', pointerEvents: 'none' }} />
                                        <input id="register-ci-nit" aria-label="Número de documento" required disabled={isLoading} maxLength={20} value={formData.ci_nit} placeholder="Ej.: 1234567"
                                            onFocus={() => setFocused('ci_nit')} onBlur={() => setFocused(null)} onChange={e => setFormData({ ...formData, ci_nit: e.target.value })} style={inputWithIconStyle('ci_nit')} />
                                    </div>
                                </div>
                                <div style={{ marginBottom: 18 }}>
                                    <DocumentoClienteFields codigo={formData.codigo_tipo_documento_identidad_sin} complemento={formData.complemento}
                                        onChange={(codigo, complemento) => setFormData({ ...formData, codigo_tipo_documento_identidad_sin: codigo, complemento })}
                                        className="reg-input" labelClassName="" disabled={isLoading} onFocusField={setFocused} onBlurField={() => setFocused(null)}
                                        labelStyle={{ fontSize: '.75rem', fontWeight: 700, color: isDark ? '#e2e8f0' : '#374151', letterSpacing: '.02em' }} inputStyleFor={inputStyle} />
                                </div>
                                {/* Submit */}
                                <button
                                    type="submit"
                                    disabled={isLoading}
                                    className="reg-btn"
                                    style={{
                                        width: '100%', marginTop: '20px',
                                        padding: '14px 24px', borderRadius: '14px',
                                        background: isLoading
                                            ? (isDark ? '#231538' : '#f1f5f9')
                                            : 'linear-gradient(135deg, #3564db 0%, #3564db 100%)',
                                        border: 'none',
                                        color: isLoading ? '#94a3b8' : '#fff',
                                        fontSize: '0.92rem', fontWeight: '800',
                                        cursor: isLoading ? 'not-allowed' : 'pointer',
                                        fontFamily: "'DM Sans', sans-serif",
                                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                                        boxShadow: isLoading ? 'none' : '0 8px 24px rgba(193,52,139,0.4)',
                                        transition: 'all 0.25s cubic-bezier(.16,1,.3,1)',
                                        letterSpacing: '-0.01em',
                                    }}
                                >
                                    {isLoading ? (
                                        <>
                                            <div style={{
                                                width: '16px', height: '16px', borderRadius: '50%',
                                                border: `2px solid #94a3b820`, borderTop: `2px solid #94a3b8`,
                                                animation: 'spin 0.7s linear infinite',
                                            }} />
                                            Creando cuenta...
                                        </>
                                    ) : (
                                        <>
                                            Crear cuenta gratis
                                            <ArrowRight size={16} strokeWidth={2.5} />
                                        </>
                                    )}
                                </button>

                                {/* TOS */}
                                <p style={{
                                    textAlign: 'center', marginTop: '14px',
                                    fontSize: '0.72rem', color: isDark ? '#cbd5e1' : '#94a3b8', fontWeight: '500', lineHeight: '1.5',
                                }}>
                                    Al registrarte aceptas nuestros{' '}
                                    <span style={{ color: isDark ? '#FFD97C' : '#3564db', fontWeight: '700', cursor: 'pointer' }}>Términos</span>
                                    {' '}y{' '}
                                    <span style={{ color: isDark ? '#FFD97C' : '#3564db', fontWeight: '700', cursor: 'pointer' }}>Privacidad</span>
                                </p>

                                {/* Link a login */}
                                <p style={{
                                    textAlign: 'center', marginTop: '20px',
                                    fontSize: '0.85rem', color: isDark ? '#cbd5e1' : '#64748b', fontWeight: '500',
                                }}>
                                    ¿Ya tienes cuenta?{' '}
                                    <Link to="/login" className="reg-link" style={{
                                        color: isDark ? '#FFD97C' : '#3564db', fontWeight: '700',
                                        textDecoration: 'none', transition: 'color 0.2s',
                                    }}>
                                        Iniciar sesión →
                                    </Link>
                                </p>
                            </form>
                        </div>
                    </BorderGlow>
                </div>
            </div>
        </div>
    );
};

export default Register;



